import { set } from "date-fns";
import { useEffect, useRef, useState } from "react";
import messageService from "../services/messageService";

interface SendMessageCompProps {
    show: boolean;
    offerId: number;
    onClose: () => void;

}

interface SendMessageFormErrors {
    subject?: string;
    message?: string;
}

const SendMessageComp: React.FC<SendMessageCompProps> = ({ show, offerId, onClose }) => {
    const modalRef = useRef<HTMLDivElement | null>(null);
    const bsModalRef = useRef<any>(null);

    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sendEmailCopy, setSendEmailCopy] = useState(true);
    const [sendMessageFormErrors, setSendMessageFormErrors] = useState<SendMessageFormErrors>({});
    const [serviceStatus, setServiceStatus] = useState({ type: '', message: '' });

    useEffect(() => {
        if (modalRef.current) {
            bsModalRef.current = new (window as any).bootstrap.Modal(modalRef.current);
            modalRef.current.addEventListener('hidden.bs.modal', onClose);
        }
        return () => {
            modalRef.current?.removeEventListener('hidden.bs.modal', onClose);
        };
    }, []);

    useEffect(() => {
        if (show) {
            setSubject('');
            setMessage('');
            setIsSubmitted(false);
            setIsSubmitting(false);
            setSendMessageFormErrors({});
            setServiceStatus({ type: '', message: '' });
            bsModalRef.current?.show();
        } else {
            bsModalRef.current?.hide();
        }
    }, [show]);

    const validateMessage = () => {
        const errors: SendMessageFormErrors = {};
        if (!subject.trim()) {
            errors.subject = 'Subject is required';
        }
        if (!message.trim()) {
            errors.message = 'Message is required';
        }
        setSendMessageFormErrors(errors);
        return Object.keys(errors).length === 0;
    }

    const handleSubmit = async (e: React.FormEvent) => {
        const sendMessageFromOffer = async (offerId: number, subject: string, message: string) => {
            try {
                const response = await messageService.sendMessageFromOffer(offerId, subject, message);
                if (response.result === 'Success') {
                    setServiceStatus({ type: 'Success', message: 'Message sent successfully.' });

                    setIsSubmitted(true);
                }
                else if (response.result === 'TooFrequent') {
                    setServiceStatus({ type: 'Error', message: 'You are sending messages too frequently. Please wait a moment and try again.' });

                }
                else if (response.result === 'Error') {
                    setServiceStatus({ type: 'Error', message: 'Failed to send message. Please try again later.' });

                }
                else {
                    throw new Error('Failed to send message');
                }

            }
            catch (error) {
                setServiceStatus({ type: 'Error', message: 'Failed to send message. Please try again later.' });

            }
            finally {
                setIsSubmitting(false);

            }
        }

        e.preventDefault();
        setServiceStatus({ type: '', message: '' });
        if (validateMessage()) {
            setIsSubmitting(true);
            sendMessageFromOffer(offerId, subject, message);
        }
    };

    return (
        <div className="modal fade" tabIndex={-1} ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content rounded-0">
                    <div className="modal-header">
                        <h5 className="modal-title">Send Message</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                            {!isSubmitted ? (
                                <>
                                    <div className="col-12 mb-3">
                                        <label htmlFor="subject" className="form-label">Subject</label>
                                        <input
                                            type="text"
                                            className={`form-control ${sendMessageFormErrors.subject ? 'is-invalid' : ''}`}
                                            id="subject"
                                            value={subject}
                                            disabled={isSubmitting}
                                            onChange={(e) => setSubject(e.target.value)}

                                        />
                                        {sendMessageFormErrors.subject && <div className="invalid-feedback">{sendMessageFormErrors.subject}</div>}
                                    </div>
                                    <div className="col-12 mb-3 mt-3">
                                        <label htmlFor="message" className="form-label">Message</label>
                                        <textarea
                                            className={`form-control ${sendMessageFormErrors.message ? 'is-invalid' : ''}`}
                                            id="message"
                                            rows={4}
                                            value={message}
                                            disabled={isSubmitting}
                                            onChange={(e) => setMessage(e.target.value)}

                                        ></textarea>
                                        <div className="form-check me-auto">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="sendEmailCopyCheckbox"
                                                checked={sendEmailCopy}
                                                disabled={isSubmitting}
                                                onChange={(e) => setSendEmailCopy(e.target.checked)}
                                            />
                                            <label
                                                className="form-check-label small text-muted"
                                                htmlFor="sendEmailCopyCheckbox"
                                            >
                                                Send buyer an email copy
                                            </label>
                                        </div>

                                        {sendMessageFormErrors.message && <div className="invalid-feedback">{sendMessageFormErrors.message}</div>}
                                    </div>
                                    {serviceStatus.type === "Error" && <div className="col-md-12"><div className="col-md-12 clearfix"><div className="alert alert-danger p-2 mb-0">{serviceStatus.message}</div></div></div>}
                                    <div className="d-flex justify-content-between mt-3">
                                        <button type="button" className="btn btn-outline-dark rounded-0 me-2" onClick={onClose}>Cancel</button>
                                        <button type="submit" className="btn btn-dark rounded-0  " disabled={isSubmitting}>
                                            {isSubmitting ? <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Sending...
                                            </> : 'Send Message'}
                                        </button>
                                    </div>

                                </>
                            ) : (
                                <>
                                    {serviceStatus.type === "Success" && <div className="col-md-12 mb-3"><div className="col-md-12 clearfix"><div className="alert alert-success p-2 mb-0">{serviceStatus.message}</div></div></div>}
                                    <button className="btn btn-outline-dark rounded-0 mb-3 float-end" onClick={onClose}>Close</button>
                                </>
                            )}
                        </div>

                    </form>

                </div>
            </div>
        </div>
    );
}
export default SendMessageComp;